/**
 * MetricExplanationModalContainer
 *
 * Container component for the metric explanation modal.
 * Handles theme detection, explanation data lookup, and safe-area resolution.
 * @remarks
 * This component orchestrates the metric explanation modal by:
 * - Looking up explanation data from metricId
 * - Determining theme (light/dark) for styling
 * - Resolving the top safe-area inset HERE, outside the RN Modal, and handing
 *   the View a plain number (SE-7396). On iOS the native Modal mounts in a
 *   separate view hierarchy, so a `SafeAreaView` nested inside its content
 *   reads a 0/racy top inset on first paint and the header (with its close
 *   button) lands under the status bar / Dynamic Island. Mirrors
 *   `FullScreenChartWrapperContainer`. Web renders a centered portal card and
 *   must not receive a device inset, so it gets 0.
 * - Flooring that native inset at the window's launch metrics (SE-8770). On a
 *   first open the live inset can read 0 for a render while the presenting
 *   route is still fading in; `initialWindowMetrics` is known before any of
 *   that and the app is portrait-locked, so it is a safe floor.
 * - Passing all computed values to the View component
 */

import { useMemo } from "react";
import { Platform } from "react-native";
import {
  initialWindowMetrics,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import MetricExplanationModalView from "./MetricExplanationModalView";

import { useTheme } from "@/providers/ThemeProvider";
import { MetricExplanationModalProps } from "@/types/metricExplanationTypes";
import { getMetricExplanation } from "@/utils/explanations";

/**
 * Container component for metric explanation modal
 * @param props - Modal props including open state, handlers, and metric data
 * @param props.isOpen - Whether the modal is currently visible
 * @param props.onClose - Callback invoked when modal should be closed
 * @param props.metricId - Unique identifier of the metric to display explanation for
 * @param props.metricValue - Numeric or string value of the metric
 * @param props.rosterValues - Array of metric values for team roster comparison
 * @param props.player - Player data including positions and categorical scores
 * @param props.listPlayerSameTeamAndPosition - Array of players in same team and position for comparison
 * @param props.isFetchingListPlayerSameTeamAndPosition - Whether comparison players data is loading
 * @param props.hostIsModalRoute - The caller is already a presented native modal route (SE-8770)
 */
function MetricExplanationModalContainer({
  isOpen,
  onClose,
  metricId,
  metricValue,
  rosterValues,
  player,
  listPlayerSameTeamAndPosition,
  isFetchingListPlayerSameTeamAndPosition,
  hostIsModalRoute,
}: MetricExplanationModalProps) {
  const insets = useSafeAreaInsets();
  const nativeTopInset = Math.max(
    insets.top,
    initialWindowMetrics?.insets.top ?? 0
  );
  const { resolvedTheme } = useTheme();
  const isDark = useMemo(() => resolvedTheme === "dark", [resolvedTheme]);
  const explanation = useMemo(
    () => (metricId ? getMetricExplanation(metricId) : null),
    [metricId]
  );

  if (!explanation) {
    return null;
  }

  return (
    <MetricExplanationModalView
      isOpen={isOpen}
      onClose={onClose}
      topInset={Platform.OS === "web" ? 0 : nativeTopInset}
      hostIsModalRoute={hostIsModalRoute}
      explanation={explanation}
      metricValue={metricValue}
      rosterValues={rosterValues}
      player={player}
      listPlayerSameTeamAndPosition={listPlayerSameTeamAndPosition}
      isFetchingListPlayerSameTeamAndPosition={
        isFetchingListPlayerSameTeamAndPosition
      }
      isDark={isDark}
    />
  );
}

export default MetricExplanationModalContainer;
